import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import type { AppDispatch } from '@/app/store/store';
import { buildLandingHref } from '@/core/utils/apiUtils';
import { AppLink } from '@/shared/components/AppLink';
import { bannerWidgetBuild } from '@/shared/features/banner-widget/config/bannerWidget.build';
import { useBrowseBanner } from '@/shared/features/banner-widget/presentation/hooks/useBrowseBanner';

/**
 * Desktop-only banner shown at the top of the PLP.
 * Picks one random banner from the browse section of the browse-page-banner API on page load.
 * Hidden on mobile.
 */
export function BrowseBannerSlot() {
  const dispatch = useDispatch<AppDispatch>();
  const { service } = useMemo(() => bannerWidgetBuild(dispatch), [dispatch]);
  const { banner, isLoading } = useBrowseBanner({ service });
  const { analytics } = useAnalytics();
  const [imageLoaded, setImageLoaded] = useState(false);

  if (isLoading) {
    return <div className="h-full w-full animate-pulse bg-gray-200" />;
  }

  if (!banner) return null;

  const imageUrl = banner.desktopImageUrl || banner.imageUrl;
  const href = buildLandingHref(banner.landingUrl);

  const handleClick = () => {
    analytics?.trackEvent('Banner Clicked', {
      'Banner Name': banner.name,
      'Banner Type': banner.bannerType ?? '',
      'Banner Position': 'Browse Banner',
    });
  };

  const img = (
    <div className="relative h-full w-full">
      {!imageLoaded && (
        <div className="absolute inset-0 animate-pulse bg-gray-200" aria-hidden />
      )}
      <img
        src={imageUrl}
        alt={banner.name}
        className="h-full w-full object-cover transition-opacity duration-200"
        style={{ opacity: imageLoaded ? 1 : 0 }}
        loading="eager"
        onLoad={() => setImageLoaded(true)}
      />
    </div>
  );

  return (
    <div className="h-full w-full">
      {href ? (
        <AppLink to={href} className="block h-full w-full" onClick={handleClick}>
          {img}
        </AppLink>
      ) : (
        img
      )}
    </div>
  );
}
